'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { ARENA_SOCIAL_LINKS } from '@/lib/arenaSocialLinks'

import S from '../ClubModeration.module.scss'

const ArrowIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    className={S.socialLinkIcon}
    aria-hidden
  >
    <path
      d="M7 17L17 7M17 7H7M17 7V17"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

type HelpfulCardProps = {
  imageSrc: string
  title: string
  description: string
  helpfulTag: string
  comingSoonLabel: string
}

function HelpfulCard({
  imageSrc,
  title,
  description,
  helpfulTag,
  comingSoonLabel
}: HelpfulCardProps) {
  return (
    <div className={S.helpfulCard}>
      <div className={S.helpfulCardBg} aria-hidden>
        <Image
          src={imageSrc}
          alt=""
          fill
          style={{ objectFit: 'cover' }}
          priority
        />
      </div>
      <span className={S.helpfulTag}>{helpfulTag}</span>
      <div className={S.helpfulCardFooter} aria-hidden>
        <div className={S.helpfulContent}>
          <h2 className={S.helpfulTitle}>{title}</h2>
          <p className={S.helpfulDesc}>{description}</p>
        </div>
      </div>
      <div className={S.helpfulCardMask} aria-hidden />
      <div
        className={S.helpfulComingSoonOverlay}
        role="status"
        aria-live="polite"
      >
        <p className={S.helpfulComingSoonText}>{comingSoonLabel}</p>
      </div>
    </div>
  )
}

export function ModerationCards() {
  const t = useTranslations('clubs.moderation')

  const arenaSocialLinks = useMemo(
    () =>
      ARENA_SOCIAL_LINKS.map(({ key, href }) => ({
        key,
        href,
        label: t(key)
      })),
    [t]
  )

  return (
    <div className={S.cardsRow}>
      <div className={S.socialCard}>
        <div className={S.socialCardBg} aria-hidden>
          <Image
            src="/assets/moderation-ball.webp"
            alt=""
            fill
            style={{ objectFit: 'cover' }}
            priority
          />
        </div>
        <div className={S.socialCardOverlay} aria-hidden />
        <div className={S.socialCardContent}>
          <span className={S.socialTag}>{t('socialTitle')}</span>
          <div className={S.socialList}>
            {arenaSocialLinks.map(({ key, href, label }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={S.socialLink}
              >
                {label}
                <ArrowIcon />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className={S.helpfulCards}>
        <HelpfulCard
          imageSrc="/assets/moderation-guide.webp"
          helpfulTag={t('helpfulTag')}
          title={t('platformGuidesTitle')}
          description={t('platformGuidesDesc')}
          comingSoonLabel={t('comingSoon')}
        />
        <HelpfulCard
          imageSrc="/assets/moderation-regbi.webp"
          helpfulTag={t('helpfulTag')}
          title={t('knowledgeBaseTitle')}
          description={t('knowledgeBaseDesc')}
          comingSoonLabel={t('comingSoon')}
        />
      </div>
    </div>
  )
}
